import React from 'react';
import { useTranslation } from 'next-i18next';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
} from '@lb-classroom/ui';
import { X } from 'lucide-react';
import TextInput from '@components/UI/atoms/form/TextInput';
import ModalMessage from '@components/UI/organisms/ModalMessage';
import type { SetTeamsModalProps } from './set-teams-modal.types';

export const SetTeamsModal = ({
  teamsEmail,
  accountEmail,
  isSubmitting,
  justSignedUp,
  teamsAccountUrl,
  message,
  messageType,
  onTeamsEmailChange,
  onUseAccountEmail,
  onSubmit,
  onCancel,
}: SetTeamsModalProps) => {
  const { t } = useTranslation('common');

  const intro = justSignedUp
    ? t('modals.setTeams.toNewUser')
    : t('modals.setTeams.toOtherUsers');

  const teamsEmailProps = {
    placeholder: t('form.teamsContactEmail'),
    className: 'teams-contact-email',
    value: teamsEmail,
    name: 'teams-contact-email',
    type: 'email',
    updateHandler: onTeamsEmailChange,
  };

  return (
    <div className="max-w-md mx-auto">
      <Card className="z-50">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle>{t('modals.setTeams.title')}</CardTitle>
          <button type="button" className="modal-close" onClick={onCancel}>
            <span className="sr-only">Close</span>
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </CardHeader>
        <CardContent>
          <ModalMessage message={message} messageType={messageType} />
          <div>
            <p>{intro}</p>
            <p className="mt-3 text-sm text-gray-600">
              {t('modals.setTeams.hint')}
            </p>
            <a
              className="mt-1 inline-block text-sm text-blue-600 underline"
              href={teamsAccountUrl}
              target="_blank"
              rel="noreferrer"
            >
              {t('modals.setTeams.createAccountLink')}
            </a>
            <form
              method="post"
              className="teams-contact-email-form"
              onSubmit={onSubmit}
            >
              <div className="control-group mb-4">
                <TextInput {...teamsEmailProps} />
                {accountEmail ? (
                  <div className="mt-2 text-right">
                    <Button
                      type="button"
                      variant="link"
                      className="p-0 h-auto"
                      onClick={onUseAccountEmail}
                    >
                      {t('modals.setTeams.sameAsAccountEmail')}
                    </Button>
                  </div>
                ) : null}
              </div>
              <div className="control-group">
                <Button
                  size="lg"
                  className="w-full mb-8"
                  type="submit"
                  disabled={isSubmitting}
                  onClick={onSubmit}
                >
                  {t('modals.setTeams.submit')}
                </Button>
                <div className="text-center">
                  <Button
                    type="button"
                    variant="link"
                    className="p-0 h-auto"
                    onClick={onCancel}
                  >
                    {t('modals.setTeams.later')}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
